import { RouterProvider, createBrowserRouter, Navigate } from 'react-router-dom';
import { paths } from '../config/paths';
import { AppRoot, AppRootErrorBoundary } from './routes/root';
import { RegisterPage } from './routes/register/register';
import { LoginPage } from './routes/login/login';
import { ResetPasswordPage } from './routes/reset-password/reset-password';
import { RenewPasswordPage } from './routes/reset-password/renew-password';
import { NotFoundRoute } from './routes/not-found';
import { Dashboard } from './routes/dashboard/dashboard';
import { CardRequestsPage } from './routes/admin/card-requests/card-requests';
import { CardDeliveryPage } from './routes/admin/card-delivery/card-delivery';
import { WithdrawRequestsPage } from './routes/admin/withdraw-requests/withdraw-requests';
import { BuyCardPage } from './routes/buy-card/buy-card';
import { BuyCardCheckingPage } from './routes/buy-card/buy-card-checking';
import { CheckChildrenPage } from './routes/check-children/check-children';
import { ReferralTreePage } from './routes/referral-tree/referral-tree';
import { SummaryPage } from './routes/admin/users/summary/summary';
import { SearchUsersPage } from './routes/admin/users/search/search-users';
import { FixReferralsPage } from './routes/admin/fix-referrals/fix-referrals';
import { TapbotBulkInsertPage } from './routes/admin/tapbot-bulk-insert/tapbot-bulk-insert';
import { useAtom } from 'jotai';
import { userAtom } from './atoms';

export const ProtectedRoute = ({ children, adminOnly = false }: { children: React.ReactNode, adminOnly?: boolean }) => {
    const [user] = useAtom(userAtom);

    if (adminOnly && !user?.has_admin_role) {
        return <Navigate to={paths.dashboard.path} replace />;
    }

    return children;
};

const router = createBrowserRouter([
    {
        path: paths.home.path,
        element: (
            <ProtectedRoute>
                <AppRoot />
            </ProtectedRoute>
        ),
        errorElement: <AppRootErrorBoundary />,
        children: [
            {
                index: true,
                element: <Navigate to={paths.dashboard.path} replace />,
            },
            {
                path: paths.dashboard.path,
                element: <Dashboard />,
            },
            {
                path: paths.buyCard.path,
                element: <BuyCardPage />,
            },
            {
                path: paths.buyCardChecking.path,
                element: <BuyCardCheckingPage />,
            },
            {
                path: paths.checkChildren.path,
                element: <CheckChildrenPage />,
            },
            {
                path: paths.referralTree.path,
                element: <ReferralTreePage />,
            },
            {
                path: paths.admin.cardRequests.path,
                element:
                    <ProtectedRoute adminOnly>
                        <CardRequestsPage />,
                    </ProtectedRoute>
            },
            {
                path: paths.admin.withdrawRequests.path,
                element:
                    <ProtectedRoute adminOnly>
                        <WithdrawRequestsPage />,
                    </ProtectedRoute>
            },
            {
                path: paths.admin.cardDelivery.path,
                element: (
                    <ProtectedRoute>
                        <CardDeliveryPage />
                    </ProtectedRoute>
                ),
            },
            {
                path: paths.admin.usersSummary.path,
                element: (
                    <ProtectedRoute adminOnly>
                        <SummaryPage />
                    </ProtectedRoute>
                ),
            },
            {
                path: paths.admin.usersSearch.path,
                element: (
                    <ProtectedRoute adminOnly>
                        <SearchUsersPage />
                    </ProtectedRoute>
                ),
            },
            {
                path: paths.admin.fixReferrals.path,
                element: (
                    <ProtectedRoute adminOnly>
                        <FixReferralsPage />
                    </ProtectedRoute>
                ),
            },
            {
                path: paths.admin.tapbotBulkInsert.path,
                element: (
                    <ProtectedRoute adminOnly>
                        <TapbotBulkInsertPage />
                    </ProtectedRoute>
                ),
            },
        ],
    },
    {
        path: paths.auth.register.path,
        element: <RegisterPage />,
        errorElement: <AppRootErrorBoundary />,
    },
    {
        path: paths.auth.login.path,
        element: <LoginPage />,
        errorElement: <AppRootErrorBoundary />,
    },
    {
        path: paths.auth.resetPassword.path,
        element: <ResetPasswordPage />,
        errorElement: <AppRootErrorBoundary />,
    },
    {
        path: paths.auth.renewPassword.path,
        element: <RenewPasswordPage />,
        errorElement: <AppRootErrorBoundary />,
    },
    {
        path: paths.error.notFound.path,
        element: <NotFoundRoute />,
    },
    {
        path: '*',
        element: <Navigate to={paths.error.notFound.path} replace />,
    },
]);

export const AppRouter = () => {
    return <RouterProvider router={router} />;
};